/**
 * Context and provider for managing identity-related information such as ID tokens
 * and user profiles within a React application.
 *
 * @module IdentityContext
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import type {
  BackstageIdentityApi,
  OpenIdConnectApi,
  ProfileInfoApi,
} from '@backstage/core-plugin-api';

type AuthApi = OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi;

/**
 * Type definition for the identity context value.
 *
 * @typedef {Object} IdentityContextType
 * @property {string | null} idToken - The ID token of the authenticated user.
 * @property {IdentityProfile | null} profile - The profile information of the authenticated user.
 * @property {IdentityApi | null} identityApi - The identity API instance, required to force user Signout
 */

/**
 * React context for identity-related information.
 * Provides default values for `idToken` and `profile` as `null`.
 */
const IdentityContext = createContext<IdentityContextType>({
  idToken: null,
  profile: null,
  identityApi: null,
  getIdToken: () => Promise.resolve(''),
});

/**
 * Custom hook to access the identity context.
 *
 * @returns {IdentityContextType} The current identity context value.
 */
export const useIdentity = () => useContext(IdentityContext);

/**
 * Provider component for the `IdentityContext`.
 * Fetches and manages identity-related data such as ID tokens and user profiles.
 *
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The child components to render within the provider.
 * @returns {JSX.Element} The `IdentityContext.Provider` component wrapping the children.
 *
 * @example
 * ```tsx
 * <IdentityProvider>
 *   <YourComponent />
 * </IdentityProvider>
 * ```
 */
export const IdentityProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [idToken, setIdToken] = useState<string | null>(null);
  const [profile, setProfile] = useState<IdentityProfile | null>(null);
  const authApiRef = useRef<AuthApi | null>(null);

  const identityApi = useApi(identityApiRef);

  useEffect(() => {
    // @ts-expect-error
    let authApi = identityApi?.target?.config?.identityApi?.authApi;
    // @ts-expect-error
    authApi = authApi || identityApi.target.authApi;
    authApiRef.current = authApi;

    const fetchIdToken = async () => {
      try {
        const [token, profile, backstageIdentity] = await Promise.all([
          authApi.getIdToken(),
          authApi.getProfile(),
          authApi.getBackstageIdentity(),
        ]);

        const userId = backstageIdentity.identity.userEntityRef.split('/')[1];

        setIdToken(token);
        setProfile({ ...profile, userId } as IdentityProfile);
      } catch (error) {
        console.error('idToken error:', error);
        setIdToken(null);
      }
    };

    fetchIdToken();

    window.addEventListener('focus', fetchIdToken);

    return () => {
      window.removeEventListener('focus', fetchIdToken);
    };
  }, [identityApi]);

  return (
    <IdentityContext.Provider
      value={{
        idToken,
        profile,
        identityApi,
        getIdToken: () => authApiRef.current?.getIdToken() ?? Promise.resolve(''),
      }}
    >
      {children}
    </IdentityContext.Provider>
  );
};
